import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Events } from '@app/core/models/event-map';
import { AuthApiService } from '@app/core/services/auth-api.service';
import { PublishSubscribeService } from '@app/core/services/publish-subscribe.service';
import { PortalUser } from '@app/shared/models/portal-user.model';
import { AxPortalResponse } from '@app/shared/models/responses.model';
import { CustomerApiService } from '@app/shared/services/customer-api.service';
import { PortalApiService } from '@app/shared/services/portal-api.service';
import { UserService } from '@app/shared/services/User.Service';
import { UserContextSelectors } from '@app/shared/state/user-context/user-context.selectors';
import { isEmailValid, splitEmails } from '@app/shared/utils/string.util';
import { Store } from '@ngxs/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ButtonModule } from "primeng/button";
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from "primeng/iconfield";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { throwError } from 'rxjs/internal/observable/throwError';
import { Subscription } from 'rxjs/internal/Subscription';
import { catchError, finalize } from 'rxjs/operators';
import { CheckBoxComponent } from '../../check-box/check-box.component';

@Component({
  selector: 'user-setup',
  imports: [FormsModule, TabsModule, CheckBoxComponent, FloatLabelModule, InputTextModule, RadioButtonModule, SelectModule, InputIconModule, IconFieldModule, FieldsetModule, MessageModule, InputGroupModule, InputGroupAddonModule, ButtonModule],
  templateUrl: './user-setup.component.html',
  styleUrl: './user-setup.component.scss',
})
export class UserSetupComponent implements OnInit {
  #destroyRef = inject(DestroyRef);
  #subscriptions: Subscription[] = [];
  #publishSubscribeService = inject(PublishSubscribeService);
  #store = inject(Store);
  #portalApiService = inject(PortalApiService);
  #customerApiService = inject(CustomerApiService);
  #userInternalService = inject(UserService);
  #authService = inject(AuthApiService);
  #modalService = inject(BsModalService);

  @Input() userToEditID?: string;
  @Input() contactToConvertID?: string;
  @Input() custAccountToConvert?: string;
  @Input() modalId: string;

  isEnabled_SalesConfirmEdi_Trygg = signal<boolean>(false);
  isEnabled_SalesConfirmEdi = signal<boolean>(false);
  private loggedInPortalUser: any = undefined;
  private contactPerson: any = undefined;
  private secondaryContactPerson: any = undefined;
  usernameMinLength = signal<number>(4);
  oldPassword = signal<string>('');
  password1 = signal<string>('');
  password2 = signal<string>('');

  errorMessage = signal<string>('');
  infoMessage = signal<string>('');
  infoMessagePassword = signal<string>('');
  errorMessagePassword = signal<string>('');
  currentSelectTab = signal<string>('contact');
  preventCloseForUnsavedUsers: boolean = true; // Force users to save their settings if never updated
  authToEdit = signal<boolean>(false);
  dirty = signal<boolean>(false);
  dirtyPassword:boolean = false;
  isAdmin = signal<boolean>(false);
  showCloseButton = signal<boolean>(true);
  isAllowedToEditUsers = signal<boolean>(false);
  allowedToEditLoading = signal<boolean>(false);
  enableLeadsAutoreply: boolean = false;
  emailToUsername = signal<boolean>(true);
  emailToDelivery = signal<boolean>(true);
  emailToOrder = signal<boolean>(true);
  emailToQuote = signal<boolean>(true);
  editUsername = signal<boolean>(false);
  usernameValid = signal<boolean>(true);
  userNameDirty = signal<boolean>(false);
  userEmailValid = signal<boolean>(true);
  userEmailDirty = signal<boolean>(false);

  userToEdit: {
    name: string;
    userName: string;
    emailAddressPersonal: string;
    preSelectedBrand: string;
    emailAddressQuote: string;
    emailAddressesOrder: string[];
    emailAddressesDeliver: string[];
    networkAlias: string;
    phone: string;
    cellularPhone: string;
    emailAsUserName: boolean;
    enabled: boolean;
    enabledOrig: boolean;
    custAcountnumSP: string;
    custAcountnumTR: string;
    userNameAsLogin: boolean;
    contactPersonId_SP: string;
    contactPersonId_TR: string;
    externalUserId: string;
    custAccount?: number;
  };

  secondaryContact: {
    name: string;
    phone: string;
    cellularPhone: string;
    emailAddressOrder: string[];
    emailAddressQuote: string;
    emailAddressDeliver: string[];
  };

  useSecondaryContact = signal<boolean>(false);
  allContactsList: any[] = [];
  secondaryContactsList: any[] = [];
  minPasswordLength = signal<number>(8);
  minPasswordComplexity = signal<number>(3);

  passwordErrorMessage:string = '';
  private readonly shortPasswordMessage: string = `Lösenordet måste vara minst ${this.minPasswordLength()} tecken`;
  private readonly credentialsInPasswordErrorMessage: string =
    'E-postadress, för-/efternamn eller användarnamn får inte vara en del av ditt lösenord';
  private readonly weakPasswordMessage: string = 'Lösenordet är för svagt';

  readonly emailError: string = 'Felaktig e-postadress';

  secondaryContactToConvert: any;
  adUserId = signal<string>('');
  adUserIdEdit = signal<string>('');
  contactToConvert = signal<string>('');
  isReseller = this.#store.selectSignal(UserContextSelectors.isReseller);
  private oldUserName: string;

  private oldContactToConvert: string;
  private oldSecondaryContactToConvert: string;
  isValidPassword1:boolean = false;
  private portalUserDetails: PortalUser | null;

  emailOrderError = signal<boolean>(false);
  emailQuoteError = signal<boolean>(false);
  emailDeliveryError = signal<boolean>(false);

  isMF = this.#store.selectSignal(UserContextSelectors.isMf);

  private permission = this.#store.selectSignal(
    UserContextSelectors.permissionsArea
  );

  userSetupEvent = output<boolean>();

  private submitAction: string;
  addUserLoading = signal<boolean>(false);

  ngOnInit(): void {
    this.registerEventSubscriptions();
    this.initUserSetup();
  }

  private registerEventSubscriptions(): void {
    this.#subscriptions.push(
      this.#publishSubscribeService
        .on(Events.UserService.SALESCONFIRM_EDI_IS_ENABLED_CHANGED)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: () => {
            this.handleEvent_UserService_SALESCONFIRM_EDI_IS_ENABLED_CHANGED();
          },
        })
    );
  }

  private initUserSetup() {
    this.getIsAllowedToEditUsers();

    this.userToEdit = {
      name: '',
      userName: '',
      emailAddressPersonal: '',
      preSelectedBrand: '',
      emailAddressQuote: '',
      emailAddressesOrder: [],
      emailAddressesDeliver: [],
      networkAlias: '',
      phone: '',
      cellularPhone: '',
      emailAsUserName: true,
      enabled: true,
      enabledOrig: true,
      custAcountnumSP: '',
      custAcountnumTR: '',
      userNameAsLogin: false,
      contactPersonId_SP: '',
      contactPersonId_TR: '',
      externalUserId: '',
    };

    this.secondaryContact = {
      name: '',
      phone: '',
      cellularPhone: '',
      emailAddressOrder: [],
      emailAddressQuote: '',
      emailAddressDeliver: [],
    };

    this.portalUserDetails = this.#userInternalService.getPortalUser_LoggedIn();
    this.loggedInPortalUser = this.#authService.getTokenUser();
    this.adUserId.set(this.loggedInPortalUser[0]);

    if (
      this.permission().find(
        (p) => p.area == 'Admin-KP' && p.action == 'manage_users'
      )?.accesslevel > 0
    ) {
      this.isAdmin.set(true);
    }

    this.initUserToEdit();

    this.isEnabled_SalesConfirmEdi.set(false);
    this.isEnabled_SalesConfirmEdi_Trygg.set(false);

    this.setIsEnabled_SalesConfirmEdi();

    this.oldUserName = this.userToEdit.userName;
    this.oldContactToConvert = this.contactToConvert();
    this.oldSecondaryContactToConvert = this.secondaryContactToConvert;

    this.dirty.set(false);
  }

  private handleEvent_UserService_SALESCONFIRM_EDI_IS_ENABLED_CHANGED() {
    this.setIsEnabled_SalesConfirmEdi();
  }

  private setIsEnabled_SalesConfirmEdi() {
    this.isEnabled_SalesConfirmEdi_Trygg.set(
      this.#userInternalService.isSalesConfirmEdiTryggEnabled()
    );

    this.isEnabled_SalesConfirmEdi.set(this.isEnabled_SalesConfirmEdi_Trygg());
  }

  private initUserToEdit() {
    // Get the user we are editing
    if (this.userToEditID && this.userToEditID != '') {
      // When opened from 'Företagsinställningar' we provide userToEdit _or_  this.contactToConvertID as argument
      this.adUserIdEdit.set(this.userToEditID);
      this.authToEdit.set(this.isAdmin());
    } else if (this.contactToConvertID && this.contactToConvertID != '0') {
      // Convert contact to user
      this.authToEdit.set(this.isAdmin());
      this.adUserIdEdit.set('0');
      this.onContactToConvertChange(this.contactToConvertID);
      this.custAccountToConvert = this.custAccountToConvert;
    } else {
      this.adUserIdEdit.set(this.loggedInPortalUser[0]);
      this.authToEdit.set(true);
    }

    if (this.authToEdit()) {
      if (this.adUserIdEdit() != '0') {
        // Setup edit of existing user (self or other)
        this.setupUserToEdit(this.adUserIdEdit());

      } else if (this.contactToConvert() && this.contactToConvert() !== '0') {
        this.setupContactToConvert();
      } else {
        this.userToEdit.userNameAsLogin = true;
        this.userToEdit.emailAsUserName = true;
      }
    } else {
      return;
      // No point in fetching data since user is not allowed to see/edit anyway. Most likely this should never happen
    }
  }

  private removeEmptyDeliveryEmail() {
    let index: number;

    while (this.userToEdit.emailAddressesDeliver.indexOf('') != -1) {
      index = this.userToEdit.emailAddressesDeliver.indexOf('');
      this.userToEdit.emailAddressesDeliver.splice(index, 1);
    }
  }

  private removeEmptyOrderEmail() {
    let index: number;

    while (this.userToEdit.emailAddressesOrder.indexOf('') != -1) {
      index = this.userToEdit.emailAddressesOrder.indexOf('');
      this.userToEdit.emailAddressesOrder.splice(index, 1);
    }
  }

  private validatePassword1() {
    this.passwordErrorMessage = '';
    const nonAlpha = /[^A-Za-z0-9 ]/;
    const upperCase = /[A-Z]/;
    const lowerCase = /[a-z]/;
    const numeric = /[0-9]/;
    let numCategories = 0;
    let retVal = true;

    if (!this.password1()) {
      this.isValidPassword1 = false;
      return false;
    }

    this.dirtyPassword = true;

    if (nonAlpha.test(this.password1())) {
      numCategories++;
    }
    if (upperCase.test(this.password1())) {
      numCategories++;
    }
    if (lowerCase.test(this.password1())) {
      numCategories++;
    }
    if (numeric.test(this.password1())) {
      numCategories++;
    }
    if (numCategories < this.minPasswordComplexity()) {
      this.passwordErrorMessage = this.weakPasswordMessage;
      retVal = false;
    }
    if (
      this.password1().length > 0 &&
      this.password1().length < this.minPasswordLength()
    ) {
      this.passwordErrorMessage = this.shortPasswordMessage;
      retVal = false;
    }

    let userNames: string[] = this.userToEdit.name.split(' ');
    userNames.push(this.userToEdit.emailAddressPersonal);
    userNames.push(this.userToEdit.userName || this.userToEdit.emailAddressPersonal);
    userNames = [...userNames, ...this.userToEdit.emailAddressesDeliver];
    userNames = [...userNames, ...this.userToEdit.emailAddressesOrder];

    userNames.forEach((n) => {
      if (this.password1().toLowerCase().includes(n.toLowerCase())) {
        this.passwordErrorMessage = `${this.credentialsInPasswordErrorMessage}: '${n}'`;
        retVal = false;
      }
    });

    this.isValidPassword1 = retVal;
    return retVal;
  }

  addEmailOrder() {
    this.userToEdit.emailAddressesOrder.push('');
    this.emailToOrder.set(false);
  }
  /**
   * Get Entities from dataservice/Factory
   */
  verifyUsername(saveCallback?: Function) {
    if (this.userToEdit.userName.length < this.usernameMinLength()) {
      this.usernameValid.set(false);
      this.userNameDirty.set(true);
      return;
    }

    this.#portalApiService
      .getIsUsernameAvailable(
        this.userToEdit.networkAlias,
        this.userToEdit.userName
      )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (axResponse) => {
          this.userNameDirty.set(false);

          if (axResponse.AllOk == true) {
            this.usernameValid.set(true);

            if (saveCallback && this.userEmailValid) {
              saveCallback(); // Do the actual save if verify succeded
            }
          } else {
            this.usernameValid.set(false);
          }
        },
        error: () => {
          this.usernameValid.set(false);
        },
      });
  }

  verifyUserEmail(nextCallback?: Function, saveCallback?: Function) {
    if (isEmailValid(this.userToEdit.emailAddressPersonal)) {
      this.#portalApiService
        .getIsUsernameAvailable(
          this.userToEdit.networkAlias,
          this.userToEdit.emailAddressPersonal
        )
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: (axResponse) => {
            this.userEmailDirty.set(false);

            if (axResponse.AllOk == true) {
              this.userEmailValid.set(true);
            } else {
              this.userEmailValid.set(false);
            }

            if (nextCallback) {
              nextCallback(saveCallback); // Call next and do the actual save if verify succeded
            }
          },
          error: () => this.userEmailValid.set(false),
        });
    } else {
      this.userEmailValid.set(false);
    }
  }

  private setupContactToConvert() {
    //contactId, this.custAccountToConvert, adDomain) {
    this.getContactPersons(
      this.contactToConvert(),
      this.contactToConvert(),
      this.contactToConvert()
    );
    this.userToEdit.enabled = true;
    this.userToEdit.enabledOrig = true;
    this.userToEdit.custAcountnumSP = this.portalUserDetails.CUST_ACCOUNTNUM_SP;
    this.userToEdit.custAcountnumTR = this.portalUserDetails.CUST_ACCOUNTNUM_TR;
    this.dirty.set(true);
    this.userToEdit.userNameAsLogin = true;
    // FIXME: check that email is unique
    this.userToEdit.userName = this.userToEdit.emailAddressQuote;
    this.userToEdit.networkAlias = '';

    // Set contactPersonId_SP or contactPersonId_TR depending on contact cust account.
    if (
      this.custAccountToConvert == this.portalUserDetails.CUST_ACCOUNTNUM_SP
    ) {
      this.userToEdit.contactPersonId_SP = this.contactToConvert();
    }

    if (
      this.custAccountToConvert == this.portalUserDetails.CUST_ACCOUNTNUM_TR
    ) {
      this.userToEdit.contactPersonId_TR = this.contactToConvert();
    }

    this.useSecondaryContact.set(true);
  }

  private setupUserToEdit(adUserEditId: string) {
    const querySucceeded = (response) => {
      const portalUser = response;

      this.userToEdit.preSelectedBrand = portalUser.PRESELECTEDBRAND;
      this.userToEdit.name = portalUser.USERFULLNAME;
      this.userToEdit.enabled = portalUser.ROLE != 0 ? true : false;
      this.userToEdit.enabledOrig = portalUser.ROLE != 0 ? true : false;
      this.userToEdit.custAcountnumSP = portalUser.CUST_ACCOUNTNUM_SP;
      this.userToEdit.custAcountnumTR = portalUser.CUST_ACCOUNTNUM_TR;

      this.userToEdit.externalUserId = portalUser.EXTERNALUSERID;
      const portalUserUpdatedDate = new Date(portalUser.UPDATEDDATETIME);

      if (
        !portalUserUpdatedDate ||
        portalUserUpdatedDate.getFullYear() == 1900
      ) {
        this.dirty.set(true);

        if (
          this.preventCloseForUnsavedUsers &&
          this.adUserId() == adUserEditId
        ) {
          this.showCloseButton.set(false);
        }
      }

      if (portalUser.USERNAME != '') {
        this.userToEdit.userName = portalUser.USERNAME;
        this.userToEdit.userNameAsLogin = true;
      } else {
        this.userToEdit.userName = portalUser.NETWORKALIAS;
        this.userToEdit.userNameAsLogin = false;
      }

      this.userToEdit.emailAddressPersonal = portalUser.USEREMAIL;

      if (this.userToEdit.userName != this.userToEdit.emailAddressPersonal) {
        this.emailToUsername.set(false);
      }

      this.userToEdit.networkAlias = portalUser.NETWORKALIAS;
      this.getContactPersons(
        portalUser.CONTACTPERSONID_SP,
        portalUser.CONTACTPERSONID_TR,
        portalUser.CONTACTPERSONID_CC
      );
    };

    this.#portalApiService
      .getPortalUserToEdit(adUserEditId, this.loggedInPortalUser[1])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: querySucceeded,
        error: () => (this.userToEdit = undefined),
      });
  }

  // Get contact person(s) for the user we are editing.
  // FIXME: Should only the the first contactperson be used and settings propagated?
  private getContactPersons(
    contactPersonId_SP: string,
    contactPersonId_TR: string,
    contactPersonId_CC: string
  ) {
    let contactPersons: any[];

    this.#customerApiService
      .getContactPersons(
        contactPersonId_SP,
        contactPersonId_TR,
        contactPersonId_CC
      )
      .pipe(
        catchError((err) => {
          return throwError(() => err);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: (response) => {
          contactPersons = response;

          this.contactPerson = contactPersons[0];

          if (this.contactPerson) {
            this.userToEdit.emailAddressQuote =
              this.contactPerson.CAP_EMAIL_CUSTPORTALQUOTATIONPRINT;

            if (
              this.userToEdit.emailAddressQuote !=
              this.userToEdit.emailAddressPersonal
            ) {
              this.emailToQuote.set(false);
            }

            // Delivery notifications might go to several receivers, separated by comma.
            // Just in case we split on semicolon as well (and strip spaces)
            this.userToEdit.emailAddressesDeliver = splitEmails(
              this.contactPerson.EMAIL3
            );
            if (
              this.userToEdit.emailAddressesDeliver.length != 1 ||
              this.userToEdit.emailAddressesDeliver[0] !=
                this.userToEdit.emailAddressPersonal
            ) {
              this.emailToDelivery.set(false);
            }
            this.userToEdit.emailAddressesOrder = splitEmails(
              this.contactPerson.EMAIL
            );
            if (
              this.userToEdit.emailAddressesOrder.length != 1 ||
              this.userToEdit.emailAddressesOrder[0] !=
                this.userToEdit.emailAddressPersonal
            ) {
              this.emailToOrder.set(false);
            }
            this.userToEdit.phone = this.contactPerson.PHONE;
            this.userToEdit.cellularPhone = this.contactPerson.CELLULARPHONE;
            this.userToEdit.custAccount = this.contactPerson.CUSTACCOUNT;
            // FIXME: Good or bad idea to set name from contact? Helps when converting contacts since we don't have a portal user name
            this.userToEdit.name = `${this.contactPerson.FIRSTNAME} ${this.contactPerson.LASTNAME}`;
          }

          if (
            this.userToEdit.custAccount.toString() ==
            this.portalUserDetails.CUST_ACCOUNTNUM_SP
          ) {
            this.setupSecondaryContactsList(
              '',
              this.portalUserDetails.CUST_ACCOUNTNUM_TR
            ); //custAccount
          }
          if (
            this.userToEdit.custAccount.toString() ==
            this.portalUserDetails.CUST_ACCOUNTNUM_TR
          ) {
            this.setupSecondaryContactsList(
              this.portalUserDetails.CUST_ACCOUNTNUM_SP,
              ''
            ); //custAccount
          }
        },
        error: (err) => {
          console.error('getContactPersons', JSON.stringify(err));
          contactPersons = undefined;
        },
      });
  }
  // Get contact person(s) for the user we are editing.
  // FIXME: Should only the the first contactperson be used and settings propagated?
  private getSecondaryContactPerson(
    contactPersonId_SP: any,
    contactPersonId_TR: any,
    contactPersonId_CC?: any
  ) {
    this.#customerApiService
      .getContactPersons(
        contactPersonId_SP,
        contactPersonId_TR,
        contactPersonId_CC
      )
      .pipe(
        catchError((err) => {
          return throwError(() => err);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: (data) => {
          this.secondaryContactPerson = data[0];
          this.secondaryContact.emailAddressQuote =
            this.secondaryContactPerson.CAP_EMAIL_CUSTPORTALQUOTATIONPRINT;

          // Delivery notifications might go to several receivers, separated by comma.
          // Just in case we split on semicolon as well (and strip spaces)
          const regexp = /\s*[,;]\s*/;

          this.secondaryContact.emailAddressDeliver =
            this.secondaryContactPerson.EMAIL3.split(regexp);
          this.secondaryContact.emailAddressOrder =
            this.secondaryContactPerson.EMAIL.split(regexp);
          this.secondaryContact.phone = this.secondaryContactPerson.PHONE;
          this.secondaryContact.cellularPhone =
            this.secondaryContactPerson.CELLULARPHONE;

          // FIXME: Good or bad idea to set name from contact? Helps when converting contacts since we don't have a portal user name
          this.secondaryContact.name = `${this.secondaryContactPerson.FIRSTNAME} ${this.secondaryContactPerson.LASTNAME}`;

          // Set contactPersonId_SP or contactPersonId_TR depending on contact cust account.
          if (
            this.secondaryContactPerson.CUSTACCOUNT ==
            this.portalUserDetails.CUST_ACCOUNTNUM_SP
          ) {
            this.userToEdit.contactPersonId_SP =
              this.secondaryContactPerson.CONTACTPERSONID;
          }
          if (
            this.secondaryContactPerson.CUSTACCOUNT ==
            this.portalUserDetails.CUST_ACCOUNTNUM_TR
          ) {
            this.userToEdit.contactPersonId_TR =
              this.secondaryContactPerson.CONTACTPERSONID;
          }
        },
        error: (err) => {
          console.error('getSecondaryContactPerson', JSON.stringify(err));
          this.secondaryContactPerson = undefined;
        },
      });
  }

  private setupSecondaryContactsList(custAccount_SP, custAccount_TR) {
    this.#portalApiService
      .getAllContactPersons(custAccount_SP, custAccount_TR)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (response) => {
          this.secondaryContactsList = response;

          for (let i: number = 0; i < this.secondaryContactsList.length; i++) {
            if (
              this.secondaryContactsList[i].custAccount.substring(0, 1) == 's'
            ) {
              this.secondaryContactsList[i].name += ' (SP)';
            }
            if (
              this.secondaryContactsList[i].custAccount.substring(0, 1) == 't'
            ) {
              this.secondaryContactsList[i].name += ' (TR)';
            }
          }
        },
      });
  }

  private modalCloseEmitter() {
    if (this.contactToConvertID || this.custAccountToConvert) {
      this.userSetupEvent.emit(false);
    }

    if (this.userToEditID) {
      this.userSetupEvent.emit(true);
    }

    this.#modalService.hide(this.modalId);
  }

  private enableUser() {
    this.#portalApiService.enableUser(this.adUserIdEdit()).subscribe({
      next: (axResponse: AxPortalResponse) => {
        if (axResponse.AllOk) {
          this.errorMessage.set('');
          this.infoMessage.set(
            'Användaruppgifter sparade och användaren aktiverad.'
          );
          this.dirty.set(false);

          this.modalCloseEmitter();
        } else {
          this.infoMessage.set('');
          this.errorMessage.set(axResponse.Message);
        }
      },
      error: () =>
        this.errorMessage.set(
          'Kunde inte kontakta servern. Försök igen senare eller kontakta supporten om problemet kvarstår'
        ),
    });
  }

  private disableUser() {
    this.#portalApiService.disableUser(this.adUserIdEdit()).subscribe({
      next: (axResponse) => {
        if (axResponse.AllOk == true) {
          this.errorMessage.set('');
          this.infoMessage.set(
            'Användaruppgifter sparade och användaren inaktiverad.'
          );
          this.dirty.set(false);
          this.modalCloseEmitter();
        } else {
          this.infoMessage.set('');
          this.errorMessage.set(axResponse.Message);
        }
      },
      error: () =>
        this.errorMessage.set(
          'Kunde inte kontakta servern. Försök igen senare eller kontakta supporten om problemet kvarstår'
        ),
    });
  }

  cancelDialog() {
    this.modalCloseEmitter();
  }


  /**
   * Check if user has user edit rights in UserInfo table in AX
   * */

  private getIsAllowedToEditUsers() {
    this.allowedToEditLoading.set(true);
    this.#portalApiService
      .getIsAllowedToEditUsers()
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => this.allowedToEditLoading.set(false))
      )
      .subscribe({
        next: (axResponse) => {
          if (axResponse.AllOk == true) {
            this.isAllowedToEditUsers.set(true);
          } else {
            this.isAllowedToEditUsers.set(false);
          }
        },
        error: () => this.isAllowedToEditUsers.set(false),
      });
  }

  userToEditNameChange() {
    if (
      !this.emailToUsername() &&
      this.oldUserName != '' &&
      this.oldUserName != this.userToEdit.userName
    ) {
      this.userNameDirty.set(true);
    }

    this.oldUserName = this.userToEdit.userName;
  }

  onContactToConvertChange(value: string) {
    this.contactToConvert.set(value);

    if (
      this.oldContactToConvert != '' &&
      this.oldContactToConvert != this.contactToConvert()
    ) {
      this.setupContactToConvert();
    }

    this.oldContactToConvert = this.contactToConvert();
  }

  onSecondaryContactToConvertChange() {
    if (
      this.oldSecondaryContactToConvert != '' &&
      this.oldSecondaryContactToConvert != this.secondaryContactToConvert
    ) {
      this.getSecondaryContactPerson(
        this.secondaryContactToConvert,
        this.secondaryContactToConvert
      );
    }

    this.oldSecondaryContactToConvert = this.secondaryContactToConvert;
  }

  enableEditUsername() {
    this.editUsername.set(true);
    this.emailToUsername.set(false);
  }

  removeEmailDelivery(item: string) {
    const index: number = this.userToEdit.emailAddressesDeliver.indexOf(item);
    this.userToEdit.emailAddressesDeliver.splice(index, 1);
    this.dirty.set(true);
  }

  removeEmailOrder(item: string) {
    const index: number = this.userToEdit.emailAddressesOrder.indexOf(item);

    this.userToEdit.emailAddressesOrder.splice(index, 1);
    this.dirty.set(true);
  }

  addEmailDelivery() {
    this.userToEdit.emailAddressesDeliver.push('');
    this.emailToDelivery.set(false);
  }

  userFields() {
    let retVal: boolean =
      this.userToEdit.name != '' && this.userToEdit.userName != '';

    if (this.userToEdit.enabled) {
      retVal = retVal && this.userToEdit.emailAddressPersonal != '';
    }

    return retVal;
  }

  newUserFields() {
    let retVal: boolean =
      this.userToEdit.name != '' &&
      this.userToEdit.emailAddressPersonal != '' &&
      this.userToEdit.userName != '';
    return retVal && this.validatePassword1();
  }

  selectTab(tab: string | number): void {
    if (tab === 'contact' || tab === 'password') {
      this.currentSelectTab.set(tab);
    }
  }

  verifyAndSave(saveCallback: string) {
    switch (saveCallback) {
      case 'addUser':
        this.verifyUserEmail(
          this.verifyUsername.bind(this),
          this.addUser.bind(this)
        );
        break;
      case 'convertContact':
        this.verifyUserEmail(
          this.verifyUsername.bind(this),
          this.convertContact.bind(this)
        );
        break;
      case 'editUser':
        this.verifyUserEmail(
          this.verifyUsername.bind(this),
          this.editUser.bind(this)
        );
        break;
    }
  }

  onSubmitForm(action: string) {
    this.submitAction = action;

    switch (this.submitAction) {
      case 'setPassword':
        this.setPassword();
        break;
      case 'savePassword':
        this.savePassword();
        break;
      case 'addUser':
        this.verifyAndSave(this.submitAction);
        break;
      case 'convertContact':
        this.verifyAndSave(this.submitAction);
        break;
    }
  }

  reloadUserToEdit() {
    this.initUserSetup();
  }

  // Update user settings in AX
  editUser() {
    if (!this.userEmailValid || !this.usernameValid()) {
      this.errorMessage.set('E-postadress eller användarnamn är ogiltigt');
      return;
    }

    const fullname: string = this.userToEdit.name;
    const spaceIndex: number = fullname.indexOf(' ');
    const firstname: string = fullname.substring(0, spaceIndex);
    const lastname: string = fullname.substring(spaceIndex + 1);

    // 'Implode' deliveryemail addresses to comma separated string
    this.removeEmptyDeliveryEmail();
    this.removeEmptyOrderEmail();

    let emailDeliver: string;
    let emailOrder: string;
    let emailQuote: string;

    if (this.emailToDelivery()) {
      emailDeliver = this.userToEdit.emailAddressPersonal;
    } else {
      emailDeliver = this.userToEdit.emailAddressesDeliver.join(',');
    }
    if (this.emailToOrder()) {
      emailOrder = this.userToEdit.emailAddressPersonal;
    } else {
      emailOrder = this.userToEdit.emailAddressesOrder.join(',');
    }
    if (this.emailToQuote()) {
      emailQuote = this.userToEdit.emailAddressPersonal;
    } else {
      emailQuote = this.userToEdit.emailAddressQuote;
    }

    /*MERGE v1.29.0 INTO v2.0.0:: SHOULD ExternalUserId be included in API call? (it is in v1.29.0 call to axPortal_EditUser)*/
    this.#portalApiService
      .editUser(
        this.adUserIdEdit(),
        this.userToEdit.userName,
        firstname,
        lastname,
        this.userToEdit.emailAddressPersonal,
        emailQuote,
        emailOrder,
        emailDeliver,
        this.userToEdit.phone,
        this.userToEdit.cellularPhone,
        this.userToEdit.preSelectedBrand,
        this.userToEdit.externalUserId
      )
      .subscribe({
        next: (axResponse) => {
          if (axResponse.AllOk == true) {
            this.errorMessage.set('');
            this.infoMessage.set('Användaruppgifter sparade.');
            this.dirty.set(false);
            if (this.userToEdit.enabled != this.userToEdit.enabledOrig) {
              if (!this.userToEdit.enabled) {
                this.disableUser();
              } else {
                this.enableUser();
              }
              this.userToEdit.enabledOrig = this.userToEdit.enabled;
            } else {
              // Only close dialog here if enable/disable has not been changed.
              // If user has been enabled/disabled we let enable()/disable() handle closing the dialog if successfull
              this.modalCloseEmitter();
            }
          } else {
            this.infoMessage.set('');
            this.errorMessage.set(axResponse.Message);
          }
        },
        error: () =>
          this.errorMessage.set(
            'Kunde inte kontakta servern. Försök igen senare eller kontakta supporten om problemet kvarstår'
          ),
      });
  }

  //FIXME: Not done. Same as save
  savePassword() {
    this.dirtyPassword = false;
    this.infoMessagePassword.set('Sparar lösenord. Det kan ta en stund...');

    this.#portalApiService
      .changePassword(this.oldPassword(), this.password1(), this.password2())
      .subscribe({
        next: (axResponse) => {
          if (axResponse.AllOk == true) {
            this.errorMessagePassword.set('');
            this.infoMessagePassword.set('Lösenord sparat!');

            setTimeout(() => {
              this.infoMessagePassword.set('');
            }, 2000);

            this.modalCloseEmitter();
          } else {
            this.dirtyPassword = true;
            this.infoMessagePassword.set('');
            this.errorMessagePassword.set(
              `Misslyckades spara lösenord. ${axResponse.Message}`
            );

            setTimeout(() => {
              this.errorMessagePassword.set('');
            }, 5000);
          }
        },
        error: () => {
          this.dirtyPassword = true;
          this.infoMessagePassword.set('');
          this.errorMessagePassword.set('Misslyckades spara lösenord.');

          setTimeout(() => {
            this.errorMessagePassword.set('');
          }, 5000);
        },
      });
  }

  setPassword() {
    this.dirtyPassword = false;
    this.infoMessagePassword.set('Sparar lösenord. Det kommer ta en stund...');

    this.#portalApiService
      .setPassword(
        this.adUserIdEdit(),
        this.password1(),
        this.password2(),
        this.userToEdit.custAcountnumSP,
        this.userToEdit.custAcountnumTR
      )
      .subscribe({
        next: (axResponse: AxPortalResponse) => {
          if (axResponse.AllOk) {
            this.password1.set('');
            this.password2.set('');
            this.errorMessagePassword.set('');
            this.infoMessagePassword.set('Lösenord sparat!');

            setTimeout(() => {
              this.infoMessagePassword.set('');
            }, 4000);
          } else {
            this.dirtyPassword = true;
            this.infoMessagePassword.set('');
            this.errorMessagePassword.set(
              `Misslyckades spara lösenord. ${axResponse.Message}`
            );

            setTimeout(() => {
              this.errorMessagePassword.set('');
            }, 5000);
          }
        },
        error: () => {
          this.dirtyPassword = true;
          this.infoMessagePassword.set('');
          this.errorMessagePassword.set('Misslyckades spara lösenord.');

          setTimeout(() => {
            this.errorMessagePassword.set('');
          }, 5000);
        },
      });
  }

  convertContact() {
    if (!this.userEmailValid || !this.usernameValid()) {
      this.errorMessage.set('E-postadress eller användarnamn är ogiltigt');
      return;
    }

    const firstname: string = this.userToEdit.name.substr(
      0,
      this.userToEdit.name.indexOf(' ')
    );
    const lastname: string = this.userToEdit.name.substr(
      this.userToEdit.name.indexOf(' ') + 1
    );
    let username: string = '';

    if (this.userToEdit.emailAsUserName) {
      username = this.userToEdit.userName;
    }

    this.removeEmptyDeliveryEmail();
    this.removeEmptyOrderEmail();

    let emailDeliver: string;
    let emailOrder: string;
    let emailQuote: string;

    if (this.emailToDelivery()) {
      emailDeliver = this.userToEdit.emailAddressPersonal;
    } else {
      emailDeliver = this.userToEdit.emailAddressesDeliver.join(',');
    }
    if (this.emailToOrder()) {
      emailOrder = this.userToEdit.emailAddressPersonal;
    } else {
      emailOrder = this.userToEdit.emailAddressesOrder.join(',');
    }
    if (this.emailToQuote()) {
      emailQuote = this.userToEdit.emailAddressPersonal;
    } else {
      emailQuote = this.userToEdit.emailAddressQuote;
    }

    let custTable_SP = this.#userInternalService.getCustTable_SP();
    let custTable_TR = this.#userInternalService.getCustTable_TR();

    let city: string = '';
    let company: string = '';

    if (custTable_TR) {
      city = custTable_TR.CITY;
      company = custTable_TR.NAME;
    }
    if (custTable_SP) {
      city = custTable_SP.CITY;
      company = custTable_SP.NAME;
    }

    this.saveUser(
      username,
      firstname,
      lastname,
      this.userToEdit.emailAddressPersonal,
      emailQuote,
      emailOrder,
      emailDeliver,
      this.userToEdit.phone,
      this.userToEdit.cellularPhone,
      this.userToEdit.preSelectedBrand,
      this.password1(),
      this.portalUserDetails.CUST_ACCOUNTNUM_SP,
      this.portalUserDetails.CUST_ACCOUNTNUM_TR,
      this.userToEdit.contactPersonId_SP,
      this.userToEdit.contactPersonId_TR,
      company,
      city,
      this.userToEdit.externalUserId
    );
  }

  addUser() {
    if (!this.userEmailValid || !this.usernameValid()) {
      this.errorMessage.set('E-postadress eller användarnamn är ogiltigt');
      return;
    }

    let firstname: string = this.userToEdit.name.substr(
      0,
      this.userToEdit.name.indexOf(' ')
    );
    let lastname: string = this.userToEdit.name.substr(
      this.userToEdit.name.indexOf(' ') + 1
    );
    let username: string = '';

    if (this.userToEdit.emailAsUserName) {
      username = this.userToEdit.userName;
    }
    // 'Implode' deliveryemail addresses to comma separated string
    this.removeEmptyDeliveryEmail();
    this.removeEmptyOrderEmail();

    let emailDeliver: string;
    let emailOrder: string;
    let emailQuote: string;

    if (this.emailToDelivery()) {
      emailDeliver = this.userToEdit.emailAddressPersonal;
    } else {
      emailDeliver = this.userToEdit.emailAddressesDeliver.join(',');
    }
    if (this.emailToOrder()) {
      emailOrder = this.userToEdit.emailAddressPersonal;
    } else {
      emailOrder = this.userToEdit.emailAddressesOrder.join(',');
    }
    if (this.emailToQuote()) {
      emailQuote = this.userToEdit.emailAddressPersonal;
    } else {
      emailQuote = this.userToEdit.emailAddressQuote;
    }

    let custTable_SP = this.#userInternalService.getCustTable_SP();
    let custTable_TR = this.#userInternalService.getCustTable_TR();
    let city = '';
    let company = '';

    if (custTable_TR) {
      city = custTable_TR.CITY;
      company = custTable_TR.NAME;
    }

    if (custTable_SP) {
      city = custTable_SP.CITY;
      company = custTable_SP.NAME;
    }

    this.saveUser(
      username,
      firstname,
      lastname,
      this.userToEdit.emailAddressPersonal,
      emailQuote,
      emailOrder,
      emailDeliver,
      this.userToEdit.phone,
      this.userToEdit.cellularPhone,
      this.userToEdit.preSelectedBrand,
      this.password1(),
      this.portalUserDetails.CUST_ACCOUNTNUM_SP,
      this.portalUserDetails.CUST_ACCOUNTNUM_TR,
      this.userToEdit.contactPersonId_SP,
      this.userToEdit.contactPersonId_TR,
      company,
      city,
      this.userToEdit.externalUserId
    );
  }

  private saveUser(
    username: string,
    firstname: string,
    lastname: string,
    emailAddressPersonal: string,
    emailAddressQuote: string,
    emailAddressOrder: string,
    emailAddressDeliver: string,
    phone: string,
    cellPhone: string,
    preSelectedBrand: string,
    password: string,
    cust_AccountNum_SP: string,
    cust_AccountNum_TR: string,
    contactPersonId_SP: string,
    contactPersonId_TR: string,
    company: string,
    city: string,
    externalUserId: string
  ) {
    const user = {
      username,
      firstname,
      lastname,
      emailAddressPersonal,
      emailAddressQuote,
      emailAddressOrder,
      emailAddressDeliver,
      phone,
      cellPhone,
      preSelectedBrand,
      password,
      cust_AccountNum_SP,
      cust_AccountNum_TR,
      cust_AccountNum_CC: '',
      contactPersonId_SP,
      contactPersonId_TR,
      contactPersonId_CC: '',
      company,
      city,
      externalUserId,
    };

    this.addUserLoading.set(true);

    this.#portalApiService
      .addUser(user)
      .pipe(finalize(() => this.addUserLoading.set(false)))
      .subscribe({
        next: (axResponse: AxPortalResponse) => {
          if (axResponse.AllOk) {
            this.errorMessage.set('');
            this.infoMessage.set('Användaruppgifter sparade.');
            this.dirty.set(false);
            this.modalCloseEmitter();
          } else {
            this.infoMessage.set('');
            this.errorMessage.set(axResponse.Message);
          }
        },
        error: (err) => {
          this.errorMessage.set(
            'Kunde inte kontakta servern. Försök igen senare eller kontakta supporten om problemet kvarstår'
          );
        },
      });
  }

  // Set dirty flag. Triggered on all inputs to know if there are changes to be saved
  // FIXME: Should we have some kind of navigation block so you can not leave this page without saving?
  change(emailType?: string, index?: number) {
    this.dirty.set(true);
    this.infoMessage.set('');
    this.infoMessagePassword.set('');

    if (emailType) {
      switch (emailType) {
        case 'emailOrder':
          this.emailOrderError.set(
            !isEmailValid(this.userToEdit.emailAddressesOrder[index])
          );
          break;
        case 'emailQuote':
          this.emailQuoteError.set(
            !isEmailValid(this.userToEdit.emailAddressQuote)
          );
          break;
        case 'emailDelivery':
          this.emailDeliveryError.set(
            !isEmailValid(this.userToEdit.emailAddressesDeliver[index])
          );
          break;
        case 'emailPersonal':
          if(this.emailToUsername()){
            this.userToEdit.userName = this.userToEdit.emailAddressPersonal
          }
          break;
      }
    }
  }

  changePassword1(newpwd1: string) {
    this.password1.set(newpwd1);
    this.dirtyPassword = true;
    this.validatePassword1();
  }
}
